// libraries
import styled, { css } from "styled-components";

// components + styles
import FormInput from "core/FormInput";
import SectionContainer from "core/SectionContainer";
import { easingSeconds } from "core/animation";
import Flexbox from "core/Flexbox";
import { A, BorderBottomLinkStyle, P, CustomFocusStyle } from "core/Typography";
import breakpoints from "core/breakpoints";
import colors from "core/colors";
import Avatar from "core/Avatar";
import { spacingWithBreakpoints } from "core/spacing";
import { medium as mediumFont } from "core/fontStyles";

// icons
import ArrowLongLeft from "core/ReusableIcons/ArrowLongLeft";
import NounLogoBrandmark from "core/ReusableIcons/NounLogoBrandmark";

// reusable measurements
const MARGIN_FOR_OVERLAP_REM = 2.5; // 40px
const WRAPPER_MOBILE_SPACING_REM = MARGIN_FOR_OVERLAP_REM + 1.5; // topSpacing xl 24px + 40 = 64px
const WRAPPER_TABLET_SPACING_REM = MARGIN_FOR_OVERLAP_REM + 1.75; // topSpacing xl 28px + 40 = 68px
const WRAPPER_DESKTOP_SPACING_REM = MARGIN_FOR_OVERLAP_REM + 2; // topSpacing xl 32px + 40 = 72px

// CheckoutLayout:
export const StyledCheckoutLayout = styled.div`
    overflow-x: hidden;
`;

export const CheckoutContentContainer = styled.div`
    background-color: ${colors.snow};
    width: 100%;
    ${({ isPrepaid }) =>
        !isPrepaid &&
        css`
            margin-top: -2.5rem; /* -40px */
        `}

    ${FormInput} {
        width: 100%;
    }

    /* for all breakpoints: */
    ${spacingWithBreakpoints.XL2TopAndBottom}

    /* mobile-only: */
    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        padding-left: 0.9375rem; /* 15px */
        padding-right: 0.9375rem; /* 15px */
    }
`;

export const CheckoutContentSection = styled(SectionContainer)`
    /* mobile-only: */
    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        > section {
            padding-left: 0;
            padding-right: 0;
        }
    }
`;

// InfoBar:
export const IconWrapper = styled.div`
    width: 1.5rem; // 24px
    text-align: center;
    color: ${colors.mist};

    svg {
        display: block; // vertically centers SVG
    }
`;

export const Detail = styled(Flexbox)`
    text-align: left;
    margin-right: 1rem; // 16px

    &:last-of-type {
        margin-right: 0;
    }

    ${Avatar} {
        margin-right: 0.5rem; // 8px
    }

    ${IconWrapper} {
        margin-right: 0.375rem; // 6px
    }

    ${A} {
        ${BorderBottomLinkStyle}
        border-color: ${colors.coin};
        color: ${colors.coin};

        &:hover {
            border-color: ${colors.mist};
            color: ${colors.mist};
        }
    }

    /* mobile-only: */
    @media (min-width: ${breakpoints.zero.rem}) and (max-width: ${breakpoints.beforeTablet.rem}) {
        width: 100%;
        margin-bottom: 0.5rem; // 8px

        &:last-of-type {
            margin-bottom: 0;
        }
    }
`;

export const Wrapper = styled(SectionContainer)`
    text-align: center;

    > section {
        padding-bottom: ${WRAPPER_MOBILE_SPACING_REM}rem;

        @media (min-width: ${breakpoints.tablet.rem}) {
            padding-bottom: ${WRAPPER_TABLET_SPACING_REM}rem;
        }
        @media (min-width: ${breakpoints.beforeDesktop
                .rem}) and (min-height: ${breakpoints.laptop.rem}) {
            padding-bottom: ${WRAPPER_DESKTOP_SPACING_REM}rem;
        }
    }
`;

export const DetailWrapper = styled(Flexbox)`
    /* mobile-only: */
    @media (min-width: ${breakpoints.zero.rem}) and (max-width: ${breakpoints
            .beforeTablet.rem}) {
        display: flex;
    }
`;

export const CapitalizeOnSmBreakpoint = styled.span`
    /* mobile-only: */
    @media (min-width: ${breakpoints.zero.rem}) and (max-width: ${breakpoints
            .beforeTablet.rem}) {
        text-transform: capitalize;
    }
`;

// Navbar:
export const NpLogoAnchor = styled(A)`
    display: inline-block;
    position: absolute;
    margin: auto;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 3rem;
    height: 2.9375rem; /* 47px */
    padding: 0.2rem;
    /*  tablet+ */
    @media (min-width: ${breakpoints.tablet.rem}) {
        width: 7.5rem; /* 120px */
        height: 6.5rem; /* 104px */
    }
`;

export const Logo = styled(NounLogoBrandmark)`
    width: 100%;
    height: 100%;
    fill: ${colors.black};
    transition: opacity ${easingSeconds};

    ${A}:hover & {
        opacity: 0.6;
    }

    ${A}:focus & {
        outline: 3px solid ${colors.focus};
    }
`;

export const BackButtonIconAndTextWrapper = styled(Flexbox)`
    /*  tablet+ */
    @media (min-width: ${breakpoints.tablet.rem}) {
        margin-bottom: 0.5rem;
    }
`;

export const BackButtonIcon = styled(ArrowLongLeft).attrs({
    size: "md"
})`
    margin-right: 0.375rem;

    path {
        fill: ${colors.fossil};
    }
`;

export const BackButtonText = styled(P)`
    color: ${colors.fossil};
`;

export const BackLink = styled.button`
    ${mediumFont}
    background: none;
    padding: none;
    border: none;
    cursor: pointer;
    display: inline-block;
    text-decoration: none;

    & p,
    ${BackButtonIcon} path {
        transition: all ${easingSeconds};
    }

    &:hover {
        & p {
            color: ${colors.ink};
        }

        ${BackButtonIcon} path {
            fill: ${colors.ink};
        }
    }
    ${CustomFocusStyle}
`;

export const Title = styled(P)`
    text-align: left;
    color: ${colors.fossil};

    /*  tablet+ */
    @media (min-width: ${breakpoints.tablet.rem}) {
        max-width: 11rem;
        overflow-wrap: break-word;
    }
`;

export const Thumbnail = styled.img`
    max-height: 1.8125rem;
    max-width: 5rem;
    margin-right: 0.5rem; /* 8px */
    height: auto;
    width: auto;
`;

export const InfoWrapper = styled(Flexbox)`
    display: none;

    /* tablet+ */
    @media (min-width: ${breakpoints.tablet.rem}) {
        display: flex;
    }
`;

export const StyledCheckoutNav = styled(SectionContainer)`
    display: flex;
    align-items: center;
    height: 3.625rem; /* 58px */

    /* tablet+ */
    @media (min-width: ${breakpoints.tablet.rem}) {
        height: 6.625rem; /* 106px */
    }
`;
